import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactEcharts from 'echarts-for-react';
import * as ExcelJS from 'exceljs';
import { useAuth } from '../../components/auth/AuthContext';
import ClientTradesGrid from '../../components/grids/ClientTradesGrid';
import { BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { clientService as clientApiService, TradingEntity } from '../../services/api/clientService';
import { logger } from '../../utils/logger';
import { useSystemDate } from '../../hooks/useSystemDate';
import './Positions.css';

const formatAmount = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1000000000) return (value / 1000000000).toFixed(2) + 'B';
  if (absValue >= 1000000) return (value / 1000000).toFixed(2) + 'M';
  if (absValue >= 1000) return (value / 1000).toFixed(1) + 'K';
  return value.toFixed(0);
};

/** Derive display product type including subtypes (Forward Starting, Asiático) */
const getProductTypeDisplay = (trade: any): string => {
  if (trade.IsForwardStarting) return 'Forward (Starting)';
  if (trade.IsAverageRateForward) return 'Forward (Asiático)';
  return trade.ProductType || '';
};

interface TradeBar {
  label: string;        // x-axis label (trade number or "Total")
  value: number;        // signed amount (+buy, -sell)
  pair: string;         // currency pair for tooltip
  direction: string;    // Buy/Sell for tooltip
  isTotal: boolean;     // true for the total bar
}

const Positions: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;

  const [entities, setEntities] = useState<TradingEntity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('all');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedTrades, setSelectedTrades] = useState<any[]>([]);
  const [selectAllTrigger, setSelectAllTrigger] = useState(0);
  const [clearAllTrigger, setClearAllTrigger] = useState(0);
  const [tradeNumberSearch, setTradeNumberSearch] = useState('');
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);
  const [splitViewMode, setSplitViewMode] = useState<'parents' | 'splits'>('parents');
  const [chartExpanded, setChartExpanded] = useState(false);
  const systemDateResult = useSystemDate(clientId);
  const maturityDateFrom = filterActiveOnly ? systemDateResult.systemDate : undefined;

  // Load trading entities for the filter
  useEffect(() => {
    const loadEntities = async () => {
      if (!clientId) return;
      try {
        const response = await clientApiService.getTradingEntities(clientId);
        if (response.success && response.data) {
          setEntities(response.data);
        }
      } catch (error) {
        logger.error('Failed to load trading entities:', error);
      }
    };
    loadEntities();
  }, [clientId]);

  const handleEntityChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedEntityId(e.target.value);
    setSelectedTrades([]);
  }, []);

  const handleMultiSelectionChanged = useCallback((trades: any[]) => {
    setSelectedTrades(trades);
  }, []);

  const handleExportCsv = useCallback(() => {
    if (selectedTrades.length === 0) return;

    const headers = [
      'Trade #', 'Mirror ID', 'Our Party', 'Bank Trade #', 'Counterparty', 'Product',
      'Trade Date', 'Value Date', 'Direction', 'CCY1', 'Amount CCY1',
      'Rate', 'Spot Price', 'Forward Points', 'CCY2', 'Amount CCY2',
      'Maturity Date', 'Fixing Reference', 'Fixing Offset',
      'Settlement Type', 'Settlement CCY', 'Payment Date',
      'CCY1 Payment Date', 'CCY2 Payment Date',
      'Grouping 1', 'Grouping 2', 'Grouping 3', 'Hedge Object',
      'Workflow State', 'Confirmation Status',
    ];

    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const rows = selectedTrades.map(t => [
      t.TradeNumber, t.MirrorId, t.OurParty, t.bankTradeNumber, t.CounterpartyName, getProductTypeDisplay(t),
      t.TradeDate, t.ValueDate, t.Direction, t.Currency1, t.QuantityCurrency1,
      t.Price, t.SpotPrice, t.ForwardPoints, t.Currency2, t.QuantityCurrency2,
      t.MaturityDate, t.FixingReference, t.FixingOffset,
      t.SettlementType, t.SettlementCurrency, t.PaymentDate,
      t.Currency1PaymentDate, t.Currency2PaymentDate,
      t.Grouping1, t.Grouping2, t.Grouping3, t.HedgeObject,
      t.workflowState, t.confirmationStatus,
    ].map(escape).join(','));

    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `positions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedTrades]);

  const handleExportExcel = useCallback(async () => {
    if (selectedTrades.length === 0) return;

    const headers = [
      'Trade #', 'Mirror ID', 'Our Party', 'Bank Trade #', 'Counterparty', 'Product',
      'Trade Date', 'Value Date', 'Direction', 'CCY1', 'Amount CCY1',
      'Rate', 'Spot Price', 'Forward Points', 'CCY2', 'Amount CCY2',
      'Maturity Date', 'Fixing Reference', 'Fixing Offset',
      'Settlement Type', 'Settlement CCY', 'Payment Date',
      'CCY1 Payment Date', 'CCY2 Payment Date',
      'Grouping 1', 'Grouping 2', 'Grouping 3', 'Hedge Object',
      'Workflow State', 'Confirmation Status',
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Positions');

    // Header row
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF2D2D2D' },
      };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });

    // Data rows
    for (const trade of selectedTrades) {
      worksheet.addRow([
        trade.TradeNumber, trade.MirrorId, trade.OurParty, trade.bankTradeNumber,
        trade.CounterpartyName, getProductTypeDisplay(trade),
        trade.TradeDate, trade.ValueDate, trade.Direction, trade.Currency1,
        trade.QuantityCurrency1 != null ? Number(trade.QuantityCurrency1) : null,
        trade.Price != null ? Number(trade.Price) : null,
        trade.SpotPrice != null ? Number(trade.SpotPrice) : null,
        trade.ForwardPoints != null ? Number(trade.ForwardPoints) : null,
        trade.Currency2,
        trade.QuantityCurrency2 != null ? Number(trade.QuantityCurrency2) : null,
        trade.MaturityDate, trade.FixingReference, trade.FixingOffset,
        trade.SettlementType, trade.SettlementCurrency, trade.PaymentDate,
        trade.Currency1PaymentDate, trade.Currency2PaymentDate,
        trade.Grouping1, trade.Grouping2, trade.Grouping3, trade.HedgeObject,
        trade.workflowState, trade.confirmationStatus,
      ]);
    }

    // Number formats for amount/rate columns
    const numFmt2 = '#,##0.00';
    const numFmt4 = '#,##0.0000';
    worksheet.getColumn(11).numFmt = numFmt2;  // Amount CCY1
    worksheet.getColumn(12).numFmt = numFmt4;  // Rate
    worksheet.getColumn(13).numFmt = numFmt4;  // Spot Price
    worksheet.getColumn(14).numFmt = numFmt4;  // Forward Points
    worksheet.getColumn(16).numFmt = numFmt2;  // Amount CCY2

    // Auto-fit column widths (approximate based on header length)
    worksheet.columns.forEach((col) => {
      const header = col.header as string || '';
      col.width = Math.max(header.length + 2, 12);
    });

    // Freeze header row
    worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `positions_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedTrades]);

  // Build trade bars: one per selected trade, sorted by currency pair, plus a total
  const tradeBars: TradeBar[] = useMemo(() => {
    if (selectedTrades.length === 0) return [];

    const bars: TradeBar[] = [];
    for (const trade of selectedTrades) {
      const ccy1 = trade.Currency1 || '';
      const ccy2 = trade.Currency2 || '';
      if (!ccy1 || !ccy2) continue;

      const pair = `${ccy1}/${ccy2}`;
      const amount = parseFloat(trade.QuantityCurrency1) || 0;
      const direction = (trade.Direction || '').toLowerCase();
      const signed = (direction === 'buy' || direction === 'compra') ? amount : -amount;
      const tradeNum = trade.TradeNumber || '?';

      bars.push({ label: tradeNum, value: signed, pair, direction: trade.Direction || '', isTotal: false });
    }

    // Sort by currency pair so trades in the same pair are adjacent
    bars.sort((a, b) => a.pair.localeCompare(b.pair) || a.label.localeCompare(b.label));

    // Add total bar
    const total = bars.reduce((s, b) => s + b.value, 0);
    bars.push({ label: 'Total', value: total, pair: '', direction: '', isTotal: true });

    return bars;
  }, [selectedTrades]);

  const chartOption = useMemo(() => {
    if (tradeBars.length === 0) return null;

    const labels = tradeBars.map(b => b.label);

    // Build waterfall ranges: [base, top] for each bar
    // Each entry is [categoryIndex, bottom, top] so ECharts can auto-scale the y-axis
    const seriesData: [number, number, number][] = [];
    let running = 0;

    for (let i = 0; i < tradeBars.length; i++) {
      const bar = tradeBars[i];
      if (bar.isTotal) {
        const lo = Math.min(0, bar.value);
        const hi = Math.max(0, bar.value);
        seriesData.push([i, lo, hi]);
      } else {
        const base = running;
        running += bar.value;
        seriesData.push([i, Math.min(base, running), Math.max(base, running)]);
      }
    }

    const barColors = tradeBars.map(b =>
      b.isTotal ? '#2196F3' : b.value >= 0 ? '#4CAF50' : '#ff6b6b'
    );

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e1e1e',
        borderColor: '#444',
        textStyle: { color: '#e0e0e0', fontSize: 13 },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex ?? 0;
          const bar = tradeBars[idx];
          const sign = bar.value >= 0 ? '+' : '';
          if (bar.isTotal) {
            return `<b>Total</b><br/>Net: ${sign}${formatAmount(bar.value)}`;
          }
          return `<b>${bar.label}</b><br/>${bar.pair} ${bar.direction}<br/>${sign}${formatAmount(bar.value)}`;
        }
      },
      grid: {
        top: 40,
        right: 20,
        bottom: 60,
        left: 80,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { color: '#b3b3b3', fontSize: 10, rotate: tradeBars.length > 8 ? 45 : 0 },
        axisLine: { lineStyle: { color: '#444' } },
        axisTick: { lineStyle: { color: '#444' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#b3b3b3',
          formatter: (v: number) => formatAmount(v),
        },
        axisLine: { lineStyle: { color: '#444' } },
        splitLine: { lineStyle: { color: '#333' } },
      },
      series: [
        {
          type: 'custom',
          renderItem: (params: any, api: any) => {
            const idx = params.dataIndex;
            const catIdx = api.value(0);
            const lo = api.value(1);
            const hi = api.value(2);
            const loPixel = api.coord([catIdx, lo]);
            const hiPixel = api.coord([catIdx, hi]);
            const bandWidth = api.size([1, 0])[0];
            const barWidth = Math.min(bandWidth * 0.6, 40);

            return {
              type: 'rect',
              shape: {
                x: loPixel[0] - barWidth / 2,
                y: hiPixel[1],
                width: barWidth,
                height: loPixel[1] - hiPixel[1],
              },
              style: { fill: barColors[idx] },
            };
          },
          encode: { x: 0, y: [1, 2] },
          data: seriesData,
          label: {
            show: tradeBars.length <= 12,
            position: 'top',
            color: '#e0e0e0',
            fontSize: 10,
            formatter: (p: any) => {
              const bar = tradeBars[p.dataIndex];
              const sign = bar.value >= 0 ? '+' : '';
              return `${sign}${formatAmount(bar.value)}`;
            },
          },
        },
      ],
    };
  }, [tradeBars]);

  return (
    <div className="positions-container">
      <div className="positions-header">
        <h1>{t('positions.title')}</h1>
        <div className="positions-header-toggle">
          <span className={`positions-toggle-label${splitViewMode === 'parents' ? ' active' : ''}`}>Ops. Madres</span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={splitViewMode === 'splits'}
              onChange={() => setSplitViewMode(prev => prev === 'parents' ? 'splits' : 'parents')}
            />
            <span className="slider"></span>
          </label>
          <span className={`positions-toggle-label${splitViewMode === 'splits' ? ' active' : ''}`}>Ops. Split</span>
        </div>
      </div>

      <div className="positions-filters">
        <div className="filter-group">
          <label>{t('positions.entity')}</label>
          <select
            className="positions-select"
            value={selectedEntityId}
            onChange={handleEntityChange}
          >
            <option value="all">{t('positions.allEntities')}</option>
            {entities.map(entity => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>{t('positions.tradeSearch', 'Trade #')}</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              className="positions-select"
              value={tradeNumberSearch}
              onChange={(e) => setTradeNumberSearch(e.target.value)}
              placeholder={t('positions.tradeSearchPlaceholder', 'Filter by trade number...')}
              style={{ width: '180px' }}
            />
            <button
              className={`positions-select-all-btn${filterActiveOnly ? ' positions-filter-active' : ''}`}
              onClick={() => setFilterActiveOnly(prev => !prev)}
              title={t('positions.filterActiveTooltip')}
            >
              {t('positions.filterActive')}
            </button>
          </div>
        </div>
        <button
          className="positions-select-all-btn"
          onClick={() => setSelectAllTrigger(t => t + 1)}
        >
          {t('positions.selectAll')}
        </button>
        <button
          className="positions-select-all-btn"
          onClick={() => setClearAllTrigger(t => t + 1)}
        >
          {t('positions.clearAll')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportCsv}
          disabled={selectedTrades.length === 0}
          title={selectedTrades.length === 0 ? t('positions.exportCsvHint') : undefined}
        >
          {t('positions.exportCsv')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportExcel}
          disabled={selectedTrades.length === 0}
          title={selectedTrades.length === 0 ? t('positions.exportExcelHint') : undefined}
        >
          {t('positions.exportExcel')}
        </button>
      </div>

      <div className={`positions-content ${chartExpanded ? '' : 'chart-collapsed'}`}>
        <div className="positions-grid-wrapper">
          <ClientTradesGrid
            refreshTrigger={refreshTrigger}
            hideUploadControls={true}
            hideConfirmationColumns={true}
            menuContext="none"
            includeInternalTrades={true}
            showParentTradeNumber={true}
            multiSelect={true}
            onMultiSelectionChanged={handleMultiSelectionChanged}
            entityFilter={selectedEntityId === 'all' ? undefined : selectedEntityId}
            suppressDefaultSort={true}
            selectAllTrigger={selectAllTrigger}
            clearAllTrigger={clearAllTrigger}
            showMirrorId={true}
            tradeNumberSearch={tradeNumberSearch || undefined}
            maturityDateFrom={maturityDateFrom}
            includeSplits={true}
            splitViewMode={splitViewMode}
          />
        </div>

        <button
          className="chart-toggle-btn"
          onClick={() => setChartExpanded(!chartExpanded)}
          title={chartExpanded ? t('positions.hideChart', 'Hide chart') : t('positions.showChart', 'Show chart')}
        >
          {chartExpanded ? <ChevronRight size={16} /> : <BarChart3 size={14} />}
          {!chartExpanded && <ChevronLeft size={14} />}
        </button>

        {chartExpanded && (
          <div className="positions-charts-panel">
            <div className="chart-section">
              <h3>{t('positions.netPosition')}</h3>
              {selectedTrades.length === 0 ? (
                <div className="chart-empty-state">
                  {t('positions.selectTrades')}
                </div>
              ) : tradeBars.length === 0 ? (
                <div className="chart-empty-state">
                  {t('positions.noPositionData')}
                </div>
              ) : (
                <ReactEcharts
                  option={chartOption}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                />
              )}
            </div>
            {selectedTrades.length > 0 && (
              <div className="positions-summary">
                <span className="summary-label">{t('positions.tradesSelected')}:</span>
                <span className="summary-value">{selectedTrades.length}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Positions;
